import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {parse} from 'papaparse';
import {ProgressComponent} from '../progress/progress.component';
import {FormBuilder, FormsModule} from '@angular/forms';
import {BehaviorSubject, Subscription, throwError} from 'rxjs';
import {ImportItem} from '../../models/import-item';
import {MatPaginator} from '@angular/material/paginator';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatSort, MatSortHeader} from '@angular/material/sort';
import {
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderCellDef,
    MatHeaderRow,
    MatHeaderRowDef,
    MatRow,
    MatRowDef,
    MatTable,
    MatTableDataSource
} from '@angular/material/table';
import {animate, state, style, transition, trigger} from '@angular/animations';
import '@firebase/firestore';
import {Item} from '../../models/item';
import {catchError, take} from 'rxjs/operators';
import {collection, doc, docData, Firestore, getDocs, query, serverTimestamp, where, writeBatch} from '@angular/fire/firestore';
import {Auth, authState} from '@angular/fire/auth';
import {MatCard, MatCardActions, MatCardContent, MatCardHeader, MatCardSubtitle, MatCardTitle} from '@angular/material/card';
import {MatButton} from '@angular/material/button';
import {MatChipListbox, MatChipOption} from '@angular/material/chips';
import {MatIcon} from '@angular/material/icon';

@Component({
    selector: 'app-item-upload',
    templateUrl: './item-upload.component.html',
    styleUrls: ['./item-upload.component.css'],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({height: '0px', minHeight: '0', display: 'none'})),
            state('expanded', style({height: '*'})),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    imports: [
        ProgressComponent,
        MatCard,
        MatCardHeader,
        MatCardTitle,
        MatCardSubtitle,
        MatCardContent,
        MatIcon,
        MatButton,
        MatChipListbox,
        MatTable,
        MatSort,
        MatSortHeader,
        MatColumnDef,
        MatHeaderCellDef,
        MatCellDef,
        MatCell,
        MatHeaderCell,
        MatHeaderRowDef,
        MatHeaderRow,
        MatRowDef,
        MatRow,
        MatPaginator,
        MatCardActions,
        MatChipOption,
        FormsModule
    ],
    standalone: true
})
export class ItemUploadComponent implements OnInit, OnDestroy {

    @ViewChild(ProgressComponent, {static: true}) progress: ProgressComponent;
    @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
    @ViewChild(MatSort, {static: true}) sort: MatSort;
    @ViewChild('fileInput', {static: true}) fileInput: ElementRef;
    itemsSubscription: Subscription;
    authStateSubscription: Subscription;
    userSubscription: Subscription;
    expandedItem: ImportItem;
    dataSource: MatTableDataSource<ImportItem> = new MatTableDataSource();
    itemsToImport: Item[] = [];
    items: BehaviorSubject<ImportItem[]> = new BehaviorSubject<ImportItem[]>([]);
    displayedColumns: string[] = [
        'tag', 'facilityName', 'locationName', 'customerPartNumber', 'eclipseId', 'itemType', 'itemDescription', 'quantity'
    ];
    now: number;
    parsed = false;
    invalid = true;
    errors = [];
    headerErrors = [];
    private tagRegex = RegExp(/^[a-zA-Z0-9-]+$/);
    private accountId: string;

    constructor(
        private formBuilder: FormBuilder,
        private firestore: Firestore,
        private snackBar: MatSnackBar,
        private fireAuth: Auth,
    ) {
        setInterval(() => {
            this.now = Date.now();
        }, 10000);
    }

    ngOnInit() {
        this.itemsSubscription = this.items.subscribe((data: ImportItem[]) => {
            this.dataSource.data = data;
            this.dataSource.paginator = this.paginator;
            this.dataSource.sort = this.sort;
        });

        this.progress.setVisible(true);
        this.authStateSubscription = authState(this.fireAuth).pipe(
            take(1),
            catchError(err => {
                this.progress.setVisible(false);
                return throwError(err);
            })
        ).subscribe(user => {
            const userDocRef = doc(this.firestore, 'users', user.uid);
            this.userSubscription = docData(userDocRef).pipe(
                catchError(err => {
                    this.progress.setVisible(false);
                    return throwError(() => err);
                })
            ).subscribe(result => {
                this.accountId = result.accountId;
                this.progress.setVisible(false);
            });

        });
    }

    submit() {
        let headersValidated = false;
        const items: ImportItem[] = [];
        this.errors = [];
        this.headerErrors = [];
        const data = this.fileInput.nativeElement;
        const csv = data.files[0];
        if (!csv) {
            return;
        }
        parse(csv, {
            header: true,
            skipEmptyLines: 'greedy',
            step: (row, parser) => {
                const theRow = row.data as ImportItem;
                theRow.tag = theRow.tag.toLowerCase();
                if (!headersValidated) {
                    parser.pause();
                    if (!('tag' in theRow)) {
                        this.headerErrors.push('tag column header is missing or formatted incorrectly');
                    }
                    if (!('eclipseId' in theRow)) {
                        this.headerErrors.push('eclipseId column header is missing or formatted incorrectly');
                    }
                    if (!('customerPartNumber' in theRow)) {
                        this.headerErrors.push('customerPartNumber column header is missing or formatted incorrectly');
                    }
                    if (!('facility' in theRow)) {
                        this.headerErrors.push('facility column header is missing or formatted incorrectly');
                    }
                    if (!('itemType' in theRow)) {
                        this.headerErrors.push('itemType column header is missing or formatted incorrectly');
                    }
                    if (!('itemDescription' in theRow)) {
                        this.headerErrors.push('itemDescription column header is missing or formatted incorrectly');
                    }
                    if (!('source' in theRow)) {
                        this.headerErrors.push('source column header is missing or formatted incorrectly');
                    }
                    if (!('destination' in theRow)) {
                        this.headerErrors.push('destination column header is missing or formatted incorrectly');
                    }
                    if (!('pull' in theRow)) {
                        this.headerErrors.push('pull column header is missing or formatted incorrectly');
                    }
                    if (!('quantity' in theRow)) {
                        this.headerErrors.push('quantity column header is missing or formatted incorrectly');
                    }
                    if (!('notes' in theRow)) {
                        this.headerErrors.push('notes column header is missing or formatted incorrectly');
                    }
                    if (this.headerErrors.length === 0) {
                        let tmp: ImportItem;
                        tmp = theRow;
                        if (!theRow.location) {
                            theRow.location = 'Shipping';
                        }
                        items.push(tmp);
                        parser.resume();
                        headersValidated = true;
                    } else {
                        parser.abort();
                    }
                } else {
                    let tmp: ImportItem;
                    tmp = theRow;
                    if (!theRow.location) {
                        theRow.location = 'Shipping';
                    }
                    items.push(tmp);
                }
            },
            complete: () => {
                if (this.headerErrors.length === 0) {
                    this.items.next(items);
                    this.parsed = true;
                }
            }
        });
    }

    async upload() {
        this.parsed = false;
        this.progress.setVisible(true);
        await this.prepareData();
        if (this.errors.length > 0) {
            this.progress.setVisible(false);
            return;
        }
        const batch = writeBatch(this.firestore);
        for (const item of this.itemsToImport) {
            batch.set(doc(collection(this.firestore, 'items'), item.uniqueItemId), item);
        }
        await batch.commit();
        this.items.next([]);
        this.progress.setVisible(false);
        this.snackBar.open('Import was Successful', null, {duration: 3000});
    }

    async prepareData() {
        for (const item of this.dataSource.data) {
            let facilityId: string;
            let locationId: string;
            const model = {} as Item;
            model.accountId = this.accountId;
            model.fifoDate = serverTimestamp();
            model.updated = serverTimestamp();
            // Unique Item ID and Tag are the same so Unique Item ID is removed
            model.uniqueItemId = item.tag;
            model.tag = item.tag;
            model.locationName = item.location;
            model.facilityName = item.facility;
            model.source = item.source;
            model.destination = item.destination;
            model.eclipseId = item.eclipseId;
            model.customerPartNumber = item.customerPartNumber;
            model.itemType = item.itemType;
            model.itemDescription = item.itemDescription;
            model.pull = item.pull;
            model.quantity = Number(item.quantity);
            model.notes = item.notes;
            model.palletized = false;
            model.palletNumber = '';
            model.flag = 0;
            // Always Available to Ship according to the requirements
            model.status = 0;
            if (!model.tag) {
                if (!this.errors.includes('Tag is required for all items')) {
                    this.errors.push('Tag is required for all items');
                }
                return;
            }
            if (!this.tagRegex.test(model.tag)) {
                this.errors.push(`Item ${model.tag}: Tag must only include alphanumeric characters and hyphens`);
            }
            if (!model.eclipseId) {
                this.errors.push(`Item ${model.tag}: Eclipse ID is required`);
            }
            if (!model.customerPartNumber) {
                this.errors.push(`Item ${model.tag}: Customer Part Number is required`);
            }
            if (!item.facility) {
                this.errors.push(`Item ${model.tag}: Facility is required`);
            }
            if (!model.itemType) {
                this.errors.push(`Item ${model.tag}: Item Type is required`);
            }
            if (!model.itemDescription) {
                this.errors.push(`Item ${model.tag}: Item Description is required`);
            }
            if (!item.quantity && item.quantity !== 0) {
                this.errors.push(`Item ${model.tag}: Quantity is required`);
            } else if (!Number.isInteger(model.quantity)) {
                this.errors.push(`Item ${model.tag}: Quantity must be an integer`);
            }
            // 1. Find the facility by name
            const facilitiesCol = collection(this.firestore, 'facilities');
            const facilityQuery = query(
                facilitiesCol,
                where('name', '==', item.facility)
            );
            const facilitySnap = await getDocs(facilityQuery);

            if (!facilitySnap.empty) {
                const facilityDoc = facilitySnap.docs[0];
                facilityId = facilityDoc.id;
                const facilityData = facilityDoc.data();

                if (facilityData.type !== 1) {
                    this.errors.push(`Item ${model.tag}: Facility type must be Branch`);
                }
                const locationsCol = collection(
                    doc(this.firestore, 'facilities', facilityId),
                    'locations'
                );
                const locationQuery = query(
                    locationsCol,
                    where('name', '==', item.location)
                );
                const locationSnap = await getDocs(locationQuery);

                if (!locationSnap.empty) {
                    const locationDoc = locationSnap.docs[0];
                    locationId = locationDoc.id;
                    const locationData = locationDoc.data();

                    if (locationData.type !== 1) {
                        this.errors.push(`Item ${model.tag}: Location type must be Shipping`);
                    }
                    model.facility = doc(this.firestore, 'facilities', facilityId).path;
                    model.facilityName = facilityData.name;
                    model.location = doc(
                        this.firestore,
                        //'facilities',
                        //facilityId,
                        'locations',
                        locationId
                    ).path;
                    model.locationName = locationData.name;
                    this.itemsToImport.push(model);
                } else {
                    this.errors.push(`Item ${model.tag}: Invalid location`);
                }
            } else {
                this.errors.push(`Item ${model.tag}: Invalid facility`);
            }

        }
    }

    ngOnDestroy(): void {
        if (this.itemsSubscription) {
            this.itemsSubscription.unsubscribe();
        }
        if (this.authStateSubscription) {
            this.authStateSubscription.unsubscribe();
        }
        if (this.userSubscription) {
            this.userSubscription.unsubscribe();
        }
    }

    checkFile($event: any) {
        this.parsed = false;
        this.errors = [];
        this.items.next([]);
        if ($event.target.files && $event.target.files[0]) {
            const file = $event.target.files[0];
            this.invalid = file.type !== 'text/csv';
            if (this.invalid) {
                this.invalid = file.name.split('.').pop() !== 'csv';
            }
        }
    }
}
